import { useEffect, useMemo } from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import TiltedCard from '../../../components/TiltedCard';
import type { Theme } from '../types';
import type { TopicSearchEntry } from '../search';
import { topicMatchesQuery, topicMatchesTag } from '../search';
import { getTagLabel } from '../tags';
import {
    resolveThematicStudiesViewMode,
    persistThematicStudiesViewMode,
    THEMATIC_STUDIES_VIEW_PARAM,
} from '../viewMode';

const NO_RESULTS_MAIN = 'No results found';
const NO_RESULTS_HINT = 'Try a different term or verse reference (e.g. 1.5.12).';

interface ThemeWithCount extends Theme {
    topicCount: number;
}

interface ThematicStudiesSearchProps {
    searchIndex: TopicSearchEntry[];
    themesWithCounts: ThemeWithCount[];
}

function TopicResultsList({ entries }: { entries: TopicSearchEntry[] }) {
    return (
        <ul className="space-y-4">
            {entries.map((entry) => (
                <li key={`${entry.themeSlug}-${entry.slug}`}>
                    <Link
                        to={`/thematic-studies/${entry.themeSlug}/${entry.slug}`}
                        className="block rounded-lg border border-gray-200 bg-white p-4 transition-colors hover:border-darkBlue hover:bg-gray-50 dark:border-gray-700 dark:bg-gray-900 dark:hover:border-lightBlue dark:hover:bg-gray-800/50"
                    >
                        <span className="text-xl font-bold text-darkBlue dark:text-lightBlue">{entry.title}</span>
                        <span className="ml-2 text-base font-bold text-lightOrange">
                            {entry.themeName}
                            {entry.primarySlokaRefs.length > 0 && <> - {entry.primarySlokaRefs.join(', ')}</>}
                        </span>
                    </Link>
                </li>
            ))}
        </ul>
    );
}

export default function ThematicStudiesSearch({ searchIndex, themesWithCounts }: ThematicStudiesSearchProps) {
    const navigate = useNavigate();
    const shouldReduceMotion = useReducedMotion();
    const [searchParams] = useSearchParams();
    const query = searchParams.get('q') ?? '';
    const selectedTagId = searchParams.get('tag') ?? '';
    const viewMode = resolveThematicStudiesViewMode(searchParams.get(THEMATIC_STUDIES_VIEW_PARAM));
    const queryTrimmed = query.trim();
    const isTagActive = queryTrimmed.length === 0 && selectedTagId.length > 0;

    useEffect(() => {
        persistThematicStudiesViewMode(viewMode);
    }, [viewMode]);

    const matches = useMemo(() => {
        if (!queryTrimmed) return [];
        return searchIndex.filter((entry) => topicMatchesQuery(entry, queryTrimmed));
    }, [searchIndex, queryTrimmed]);

    const tagMatches = useMemo(() => {
        if (!isTagActive) return [];
        return searchIndex.filter((entry) => topicMatchesTag(entry, selectedTagId));
    }, [searchIndex, selectedTagId, isTagActive]);

    const showThemeCards = viewMode === 'themes' && queryTrimmed.length === 0 && !isTagActive;
    const showAllTopics = viewMode === 'all' && queryTrimmed.length === 0 && !isTagActive;
    const showNoResults = queryTrimmed.length > 0 && matches.length === 0;
    const showFilteredResults = queryTrimmed.length > 0 && matches.length > 0;
    const showTagResults = isTagActive && tagMatches.length > 0;
    const showTagNoResults = isTagActive && tagMatches.length === 0;
    const allTopicsCount = searchIndex.length;
    const allThemesCount = themesWithCounts.length;

    // Whichever list is on screen right now: all topics, a keyword match, or a tag match.
    const visibleEntries = showAllTopics ? searchIndex : showTagResults ? tagMatches : matches;
    const visibleEntriesCount = visibleEntries.length;

    return (
        <div className="m-8 min-h-screen pb-14 md:mx-28 md:my-10">
            {showThemeCards && (
                <div className="mb-12 text-center">
                    <h1 className="mb-4 text-4xl font-bold text-darkBlue dark:text-lightBlue md:text-5xl">
                        Thematic Studies
                    </h1>
                    <p className="mx-auto max-w-3xl text-xl leading-relaxed text-default-600 dark:text-default-400">
                        Discover timeless wisdom from the ācāryas, organized by theme.
                    </p>
                    <p className="mt-1 text-default-500 dark:text-default-400">
                        Found {allTopicsCount} {allTopicsCount === 1 ? 'topic' : 'topics'} across {allThemesCount}{' '}
                        {allThemesCount === 1 ? 'theme' : 'themes'}
                    </p>
                </div>
            )}
            {showAllTopics && (
                <div className="mb-8 text-center">
                    <h2 className="mb-2 text-2xl font-bold text-darkBlue dark:text-lightBlue md:text-3xl">All Topics</h2>
                    <p className="text-default-500 dark:text-default-400">
                        Found {allTopicsCount} {allTopicsCount === 1 ? 'topic' : 'topics'} across {allThemesCount}{' '}
                        {allThemesCount === 1 ? 'theme' : 'themes'}
                    </p>
                </div>
            )}

            <div className="min-h-[200px]">
                {showThemeCards && (
                    <motion.div className="grid justify-items-center gap-x-16 gap-y-16 lg:grid-cols-2 xl:grid-cols-3">
                        {themesWithCounts.map((theme, index) => (
                            <motion.div
                                key={theme.id}
                                initial={
                                    shouldReduceMotion
                                        ? { opacity: 1 }
                                        : {
                                              opacity: 0,
                                              x: index % 2 === 0 ? -50 : 50,
                                              y: -50,
                                          }
                                }
                                animate={{ opacity: 1, x: 0, y: 0 }}
                                transition={{
                                    duration: shouldReduceMotion ? 0 : 0.3,
                                    delay: shouldReduceMotion ? 0 : index * 0.2,
                                }}
                                className="justify-self-center"
                            >
                                <TiltedCard
                                    imageSrc={theme.imageSrc}
                                    name={theme.name}
                                    order={theme.topicCount}
                                    onClick={() => navigate(`/thematic-studies/${theme.slug}`)}
                                    containerHeight="420px"
                                    containerWidth="360px"
                                    scaleOnHover={1.05}
                                    rotateAmplitude={10}
                                    showTooltip={false}
                                    displayOverlayContent
                                    overlayContent={
                                        <div className="absolute bottom-20 left-0 right-0 px-3 text-center">
                                            <p className="inline-block rounded-lg bg-black/50 px-3 py-1 text-sm font-medium text-white/90 backdrop-blur-sm">
                                                <span className="font-inter-numbers">{theme.topicCount}</span> topics
                                            </p>
                                        </div>
                                    }
                                />
                            </motion.div>
                        ))}
                    </motion.div>
                )}
                {(showNoResults || showTagNoResults) && (
                    <motion.div
                        className="flex flex-col items-center justify-center gap-2 py-12 text-center"
                        initial={shouldReduceMotion ? { opacity: 1 } : { opacity: 0, y: 6 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: shouldReduceMotion ? 0 : 0.16, ease: 'easeOut' }}
                    >
                        <p className="text-2xl font-semibold text-default-700 dark:text-default-300 md:text-3xl">
                            {NO_RESULTS_MAIN}
                        </p>
                        <p className="text-base text-default-500 dark:text-default-400">
                            {showTagNoResults ? 'Try selecting a different tag.' : NO_RESULTS_HINT}
                        </p>
                    </motion.div>
                )}
                {(showFilteredResults || showAllTopics || showTagResults) && (
                    <motion.div
                        initial={shouldReduceMotion ? { opacity: 1 } : { opacity: 0, y: 6 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: shouldReduceMotion ? 0 : 0.16, ease: 'easeOut' }}
                    >
                        {(showFilteredResults || showTagResults) && (
                            <div className="mb-8 text-center">
                                <h2 className="mb-2 text-2xl font-bold text-darkBlue dark:text-lightBlue md:text-3xl">
                                    {showTagResults ? (
                                        <>
                                            Topics tagged &quot;
                                            <span className="text-lightOrange">{getTagLabel(selectedTagId)}</span>
                                            &quot;
                                        </>
                                    ) : (
                                        <>
                                            Search Results for &quot;
                                            <span className="text-lightOrange">{queryTrimmed}</span>
                                            &quot;
                                        </>
                                    )}
                                </h2>
                                <p className="text-default-500 dark:text-default-400">
                                    Found {visibleEntriesCount} {visibleEntriesCount === 1 ? 'topic' : 'topics'}
                                </p>
                            </div>
                        )}
                        <TopicResultsList entries={visibleEntries} />
                    </motion.div>
                )}
            </div>
        </div>
    );
}
